// ui/services/approvalFlowService.ts
import { db, serverTimestamp, doc, setDoc, getDoc, updateDoc } from '../../firebaseClient';
import {
  ApprovalModule,
  ApprovalFlow,
  ApprovalStage,
  ApprovalStageKey,
  ApprovalFlowStatus,
  DeptDecision
} from '../types/approval';
import { logActivity } from './activityLog';
import { rescueContractFlow } from "./contractFlowRescueReader";

const COLLECTION = 'approvalFlows';

// 顯示用：把帳號代號轉成職稱（避免在簽核歷程看到 ops / gm）
function normalizeApproverName(name?: string) {
  if (!name) return name;
  const raw = String(name).trim();
  const lower = raw.toLowerCase();
  if (lower.includes('ops')) return '財務長';
  if (lower.includes('gm')) return '總經理';
  return raw;
}


// --- Helper: Firestore does NOT allow undefined values in payloads ---
function deepOmitUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    // Firestore rejects undefined inside arrays; drop them.
    return (value as any[])
      .filter((v) => v !== undefined)
      .map((v) => deepOmitUndefined(v)) as any;
  }

  if (value && typeof value === "object") {
    const ctor = (value as any).constructor;
    // Keep non-plain objects (FieldValue, Timestamp, Date, etc.) intact
    if (ctor && ctor !== Object) return value;

    const out: any = {};
    Object.keys(value as any).forEach((k) => {
      const v: any = (value as any)[k];
      if (v === undefined) return;
      out[k] = deepOmitUndefined(v);
    });
    return out;
  }

  return value;
}


export function buildThreeStageFlow(
  module: ApprovalModule,
  targetId: string,
  createdBy: string,
  createdDept?: string,
  assigneeName?: string,
  coReviewDepts?: string[]
): ApprovalFlow {

  // ✅ 用印專用：流程固定 法務審查 → 財務長 → 總經理
  if (module === 'SEAL_REQUEST') {
    const stages: ApprovalStage[] = [
      {
        key: 'LEGAL_REVIEW',
        label: '法務審查',
        approverRole: 'legal',
        status: 'NOT_STARTED',
      },
      {
        key: 'MANAGER_OPS',
        label: '主管核決 (財務長)',
        approverRole: 'ops',
        status: 'NOT_STARTED',
      },
      {
        key: 'MANAGER_GM',
        label: '主管核決 (總經理)',
        approverRole: 'gm',
        status: 'NOT_STARTED',
      }
    ];

    return {
      id: `${module}_${targetId}`,
      module,
      targetId,
      createdBy,
      createdDept,
      assigneeName,
      createdAt: new Date().toISOString(),
      status: 'IN_PROGRESS',
      currentStageKey: 'LEGAL_REVIEW',
      stages,
    };
  }

  const stages: ApprovalStage[] = [];

  // 1. Optional CO_REVIEW Stage (Only for CONTRACT_REVIEW with selected depts)
  if (module === 'CONTRACT_REVIEW' && coReviewDepts && coReviewDepts.length > 0) {
    const initialDecisions: Record<string, DeptDecision> = {};
    coReviewDepts.forEach(deptId => {
      initialDecisions[deptId] = { status: 'PENDING' };
    });

    stages.push({
      key: 'CO_REVIEW',
      label: '多部門會辦',
      approverRole: 'multi-dept',
      status: 'PENDING', // Start immediately
      coReviewDepts: coReviewDepts,
      deptDecisions: initialDecisions
    });
  }

  // 2. Standard Stages (Legal -> 財務長 -> 總經理)
  stages.push(
    {
      key: 'LEGAL_REVIEW',
      label: '法務審查',
      approverRole: 'legal',
      // If there is a Co-Review, Legal is NOT_STARTED, otherwise PENDING
      status: 'NOT_STARTED',
    },
    {
      key: 'MANAGER_OPS',
      label: '主管核決 (財務長)',
      approverRole: 'ops',
      status: 'NOT_STARTED',
    },
    {
      key: 'MANAGER_GM',
      label: '主管核決 (總經理)',
      approverRole: 'gm',
      status: 'NOT_STARTED',
    }
  );

  return {
    id: `${module}_${targetId}`,
    module,
    targetId,
    createdBy,
    createdDept,
    assigneeName,
    createdAt: new Date().toISOString(),
    status: 'IN_PROGRESS',
    currentStageKey: stages[0].key, // Start at first stage
    stages,
  };
}

// --- Frontend Todo Sync Helper (Ensures visual updates without backend latency) ---
async function syncTodoFrontend(flow: ApprovalFlow, parentTitle?: string, parentDocCode?: string, currentStatus?: string) {
  let todoId = `${flow.module}_${flow.targetId}`;
  if (flow.module === 'SEAL_REQUEST') {
    todoId = `SEAL_REQUEST_${flow.targetId}`;
  }

  if (!parentTitle) {
    const colMap: Record<string, string> = {
      'SEAL_REQUEST': 'approval_requests',
      'CONTRACT_REVIEW': 'approval_requests',
      'OFFICIAL_REPLY': 'approval_requests',
      'GOV_PROJECT': 'compliance_governance_projects_v2'
    };
    const colName = colMap[flow.module] || 'approval_requests';

    try {
      const snap = await getDoc(doc(db, colName, flow.targetId));
      if (snap.exists) {
        const d = snap.data();
        parentTitle = (d as any)?.title || "未命名文件";
        parentDocCode = (d as any)?.docCode || (d as any)?.contractNo || flow.targetId;
      }
    } catch (e) {/* ignore */}
  }

  const status = currentStatus || flow.status;
  const isClosed = ['APPROVED', 'REJECTED', 'COMPLETED', 'RETURNED', 'FULLY_APPROVED', 'CANCELLED'].includes(status);

  await setDoc(doc(db, 'todos', todoId), deepOmitUndefined({
    module: flow.module,
    entityId: flow.targetId,
    docNo: parentDocCode || flow.targetId,
    title: parentTitle || `Flow ${flow.targetId}`,
    assigneeName: flow.assigneeName,
    overallStatus: status,
    todoStatus: isClosed ? 'CLOSED' : 'OPEN',
    updatedAt: serverTimestamp(),
    syncedBy: 'frontend_v2'
  }), { merge: true });
}

export async function createOrResetApprovalFlow(
  module: ApprovalModule,
  targetId: string,
  createdBy: string,
  createdDept?: string,
  assigneeName?: string,
  options?: { coReviewDepts?: string[] }
) {
  const flow = buildThreeStageFlow(
    module,
    targetId,
    createdBy,
    createdDept,
    assigneeName,
    options?.coReviewDepts
  );

  const flowRef = doc(db, COLLECTION, flow.id);
  await setDoc(flowRef, deepOmitUndefined({
    ...flow,
    createdAt: serverTimestamp(),
  }));

  await syncTodoFrontend(flow).catch(console.error);

  const auditModule = (module === 'SEAL_REQUEST' || module === ('STAMP_REQUEST' as any)) ? 'seal' : module;

  await logActivity({
    module: auditModule as any,
    action: "start_approval_flow",
    targetId: targetId,
    entityId: flow.id,
    entityType: "ApprovalFlow",
    actorId: createdBy,
    actorName: assigneeName || "System",
    details: `Started approval flow for ${module}`
  });

  return flow;
}

export async function getApprovalFlow(flowId: string) {
  const flowRef = doc(db, COLLECTION, flowId);
  const snap = await getDoc(flowRef);
  if (!snap.exists) return null;
  return snap.data() as ApprovalFlow;
}

/**
 * Wrapper for Contract Review flow resolution
 */
export async function getApprovalFlowForContractReview(reviewId: string, approvalFlowId?: string) {
  return rescueContractFlow(reviewId, approvalFlowId);
}

/**
 * Explicitly resets an existing flow back to the beginning for resubmission.
 */
export async function resetFlowForResubmission(flowId: string) {
  const flowRef = doc(db, COLLECTION, flowId);
  const snap = await getDoc(flowRef);
  if (!snap.exists) return;

  const flow = snap.data() as ApprovalFlow;

  // Use standard builder to ensure logic is identical
  const newFlow = buildThreeStageFlow(flow.module, flow.targetId, flow.createdBy, flow.createdDept, flow.assigneeName);

  await updateDoc(flowRef, deepOmitUndefined({
    status: 'IN_PROGRESS',
    currentStageKey: newFlow.currentStageKey,
    stages: newFlow.stages,
    updatedAt: serverTimestamp()
  }));

  await syncTodoFrontend({ ...newFlow, status: 'IN_PROGRESS' }).catch(console.error);

  await logActivity({
    module: (flow.module === 'SEAL_REQUEST' ? 'seal' : flow.module) as any,
    action: "reset_flow",
    entityId: flowId,
    entityType: "ApprovalFlow",
    details: "Flow reset for resubmission by requester"
  });
}

export async function updateStageDecision(params: {
  flowId: string;
  stageKey: ApprovalStageKey;
  decision: 'APPROVED' | 'REJECTED' | 'RETURNED' | 'DONE';
  comment: string;
  actorUid: string;
  actorName: string;
  actorEmail?: string;
  deptCode?: string;
  isDirectSign?: boolean;
}) {
  const { flowId, stageKey, decision, comment, actorUid, actorName, actorEmail, deptCode, isDirectSign } = params;

  const flowRef = doc(db, COLLECTION, flowId);
  const snap = await getDoc(flowRef);
  if (!snap.exists) return;

  const flow = snap.data() as ApprovalFlow;
  let status: ApprovalFlowStatus = flow.status;
  let currentStageKey: ApprovalStageKey | undefined | null = flow.currentStageKey;

  const stages = [...flow.stages];
  const stageIndex = stages.findIndex(s => s.key === stageKey);
  if (stageIndex === -1) return;

  const stage = { ...stages[stageIndex] } as any;
  const nowStr = new Date().toISOString();
  const isEditing = stage.status !== 'PENDING' && stage.status !== 'NOT_STARTED';

  if (stage.key === 'CO_REVIEW') {
    if (!deptCode || !stage.deptDecisions || !stage.deptDecisions[deptCode]) {
      console.error("Missing deptCode for Co-Review update");
      return;
    }

    const currentDecision = stage.deptDecisions[deptCode];
    const isCoReviewEditing = currentDecision.status === 'DONE';

    stage.deptDecisions[deptCode] = {
      ...currentDecision,
      status: 'DONE',
      comment,
      decidedBy: actorName,
      decidedAt: nowStr,
      actorUid,
      actorName,
            edited: isCoReviewEditing,
      ...(isCoReviewEditing ? { editedAt: nowStr, editedBy: actorName } : {}),
    };

    // (Optional metadata; keep TS safe by writing into stage, not DeptDecision)
    stage.lastActorEmail = actorEmail || stage.lastActorEmail;
    stage.lastIsDirectSign = typeof isDirectSign === 'boolean' ? isDirectSign : stage.lastIsDirectSign;

    if (stage.status !== 'APPROVED') {
      const allDone = Object.values(stage.deptDecisions).every((d: any) => d.status === 'DONE');
      if (allDone) {
        stage.status = 'APPROVED';
        currentStageKey = 'LEGAL_REVIEW';
        const nextIdx = stages.findIndex(s => s.key === 'LEGAL_REVIEW');
        if (nextIdx !== -1) (stages[nextIdx] as any).status = 'NOT_STARTED';
      }
    }

    stages[stageIndex] = stage;
  } else {
    stage.status = decision as any;
    stage.comment = comment;
    stage.approverUserId = actorUid;
    stage.approverName = normalizeApproverName(actorName);
    stage.actedAt = nowStr;

    // Optional metadata (safe)
    stage.actorEmail = actorEmail || stage.actorEmail;
    stage.isDirectSign = typeof isDirectSign === 'boolean' ? isDirectSign : stage.isDirectSign;

    if (isEditing) {
      stage.edited = true;
      stage.editedAt = nowStr;
      stage.editedBy = actorName;
    }

    stages[stageIndex] = stage;

    if (decision === 'REJECTED' || decision === 'RETURNED') {
      status = 'REJECTED';
      currentStageKey = null;
    } else {
      if (status === 'REJECTED') {
        status = 'IN_PROGRESS';
      }

      if (stageKey === 'LEGAL_REVIEW') {
        currentStageKey = 'MANAGER_OPS';
        const nextIdx = stages.findIndex(s => s.key === 'MANAGER_OPS');
        if (nextIdx !== -1 && (stages[nextIdx] as any).status === 'NOT_STARTED') {
          (stages[nextIdx] as any).status = 'NOT_STARTED';
        }
      } else if (stageKey === 'MANAGER_OPS') {
        currentStageKey = 'MANAGER_GM';
        const nextIdx = stages.findIndex(s => s.key === 'MANAGER_GM');
        if (nextIdx !== -1 && (stages[nextIdx] as any).status === 'NOT_STARTED') {
          (stages[nextIdx] as any).status = 'NOT_STARTED';
        }
      } else if (stageKey === 'MANAGER_GM') {
        status = 'COMPLETED';
        currentStageKey = null;
      }
    }
  }

  await updateDoc(flowRef, deepOmitUndefined({
    stages,
    status,
    currentStageKey: currentStageKey === undefined ? null : currentStageKey,
    updatedAt: serverTimestamp()
  }));

  await syncTodoFrontend({ ...flow, status }, undefined, undefined, status).catch(console.error);

  if (['SEAL_REQUEST', 'CONTRACT_REVIEW', 'OFFICIAL_REPLY'].includes(flow.module)) {
    try {
      // 注意：RETURNED 必須優先回寫，不然會被 status==='REJECTED' 蓋掉
      const parentStatus =
        decision === 'RETURNED' ? 'RETURNED' :
          status === 'COMPLETED' ? 'FULLY_APPROVED' :
            decision === 'REJECTED' ? 'REJECTED' :
              'IN_REVIEW';

      await updateDoc(doc(db, 'approval_requests', flow.targetId), deepOmitUndefined({
        status: parentStatus,
        updatedAt: serverTimestamp()
      }));
    } catch (e) {
      console.error("Failed to write back status to parent doc", e);
    }
  }

  const auditModule = (flow.module === 'SEAL_REQUEST' || flow.module === ('STAMP_REQUEST' as any)) ? 'seal' : flow.module;

  await logActivity({
    module: auditModule as any,
    action: isEditing ? `${decision}_EDIT` : decision,
    targetId: flow.targetId,
    entityId: flowId,
    entityType: "ApprovalFlow",
    actorId: actorUid,
    actorName: actorName,
    details: `${(stage as any).label || stageKey} - ${comment || '(No comment)'}${actorEmail ? ` [${actorEmail}]` : ''} ${(isDirectSign ? '(DirectSign)' : '')} ${isEditing ? '(Edited)' : ''}`.trim()
  });
}

// ------------------------------------------------------------
// ✅ Backward-compatible named exports
//
// Some pages in older branches import the following symbols.
// To keep the build stable (especially in Cloudflare Pages / Vite Rollup),
// we provide thin wrappers / aliases.
// ------------------------------------------------------------

// createOrResetFlow / createApprovalFlow historically pointed to the same behavior.
export const createOrResetFlow = createOrResetApprovalFlow;
export const createApprovalFlow = createOrResetApprovalFlow;

// updateApprovalStageDecision / updateDecision historically pointed to the stage decision updater.
export async function updateApprovalStageDecision(
  params: Parameters<typeof updateStageDecision>[0]
) {
  return updateStageDecision(params);
}

export async function updateDecision(
  params: Parameters<typeof updateStageDecision>[0]
) {
  return updateStageDecision(params);
}

